/**
 * =============================================================================
 * TODO STACK COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 * 
 * Displays pending alerts as a clean list of action cards.
 * 
 * DESIGN SYSTEM:
 * - 8pt spacing grid
 * - Semantic left indicators for severity
 * - Reduced badge density (single tag max)
 * - Muted secondary text
 * - Card title: 15-16px
 * - No decorative borders
 */

import { Box, Stack, Text, Group, Loader } from '@mantine/core';
import {
    CheckCircle,
    Warning,
    CaretRight,
    Sparkle,
} from '@phosphor-icons/react';
import ContextPills from '@components/common/ContextPills';
import { motion, AnimatePresence } from 'framer-motion';
import { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { fetchAlertsByCategory } from './api';
import type { AlertItem } from './types';
import { Priority, AlertStatus, GeneratedBy } from './types';

// =============================================================================
// DESIGN TOKENS
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 20,
    lg: 24,
} as const;

// Semantic colors ONLY - No decorative use
const semanticColors = {
    critical: 'rgb(205, 101, 101)',    // Red - Urgent
    warning: 'rgb(219, 182, 88)',      // Amber - High
    info: 'rgb(1, 149, 255)',          // Blue - Medium/Low
    positive: 'rgb(45, 187, 136)',     // Green - Success
} as const;

// Map priority to semantic severity
const priorityToSeverity: Record<Priority, keyof typeof semanticColors> = {
    [Priority.URGENT]: 'critical',
    [Priority.HIGH]: 'warning',
    [Priority.MEDIUM]: 'info',
    [Priority.LOW]: 'info',
};

interface TodoStackProps {
    /** Called when user clicks an alert card */
    onAlertClick: (alert: AlertItem) => void;
}

// =============================================================================
// ALERT CARD COMPONENT - Simplified
// =============================================================================
interface StackCardProps {
    alert: AlertItem;
    index: number;
    onClick: () => void;
}

const AlertCard: React.FC<StackCardProps> = memo(({ alert, index, onClick }) => {
    const severity = priorityToSeverity[alert.priority];
    const severityColor = semanticColors[severity];
    
    // Format due date - minimal display
    const formattedDueDate = useMemo(() => {
        if (!alert.dueDate) return null;
        const date = new Date(alert.dueDate);
        const now = new Date();
        const diffMs = date.getTime() - now.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffMs < 0) return { text: 'Overdue', isOverdue: true };
        if (diffHours < 24) return { text: `${diffHours}h left`, isOverdue: false };
        if (diffDays < 7) return { text: `${diffDays}d left`, isOverdue: false };
        return null;
    }, [alert.dueDate]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ 
                type: 'spring', 
                stiffness: 400, 
                damping: 30,
                delay: index * 0.03 
            }}
            className="w-full"
        >
            <Box
                className="relative cursor-pointer group"
                style={{
                    backgroundColor: 'white',
                    borderRadius: 12,
                    padding: spacing.md,
                    paddingLeft: spacing.lg,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden',
                }}
                onClick={onClick}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                    e.currentTarget.style.transform = 'translateY(0)';
                }}
            >
                {/* Semantic severity indicator - left bar */}
                <Box 
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        backgroundColor: severityColor,
                        borderRadius: '12px 0 0 12px',
                    }}
                />

                {/* Header row - Category only */}
                <Group className="justify-between items-start" style={{ marginBottom: spacing.xs }}>
                    <Text 
                        className="uppercase tracking-wider text-slate-400"
                        style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em' }}
                    >
                        {alert.alertTypeLabel}
                    </Text>

                    {/* Due date - subtle indicator */}
                    {formattedDueDate && (
                        <Text 
                            style={{ 
                                fontSize: 11, 
                                fontWeight: 500,
                                color: formattedDueDate.isOverdue ? semanticColors.critical : 'rgb(155, 155, 155)'
                            }}
                        >
                            {formattedDueDate.text}
                        </Text>
                    )}
                </Group>

                {/* Title - 15px */}
                <Text
                    className="text-slate-900 line-clamp-2"
                    style={{
                        fontSize: 15,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        marginBottom: alert.contextFilters ? 0 : spacing.sm,
                    }}
                >
                    {alert.title}
                </Text>

                {/* Context pills — show filters used to evaluate this alert */}
                {alert.contextFilters && (
                    <Box style={{ marginBottom: spacing.sm }}>
                        <ContextPills filters={alert.contextFilters} />
                    </Box>
                )}

                {/* Action hint - minimal */}
                <Group 
                    style={{ 
                        gap: 4, 
                        paddingTop: spacing.xs,
                        borderTop: '1px solid rgba(0,0,0,0.04)' 
                    }}
                >
                    <Text 
                        className="text-slate-500"
                        style={{ fontSize: 12, fontWeight: 500 }}
                    >
                        Take action
                    </Text>
                    <CaretRight size={12} weight="bold" className="text-slate-400" />
                </Group>
            </Box>
        </motion.div>
    );
});

AlertCard.displayName = 'AlertCard';

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const TodoStack: React.FC<TodoStackProps> = memo(({ onAlertClick }) => {
    const [alerts, setAlerts] = useState<AlertItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch only pending/actionable alerts - always filter by USER created
    useEffect(() => {
        const loadAlerts = async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Always fetch user-created alerts only
                const categorized = await fetchAlertsByCategory(GeneratedBy.USER);
                const pendingAlerts = categorized.pending;
                
                // Debug: Check for duplicate IDs
                const ids = pendingAlerts.map(a => a.id);
                const uniqueIds = new Set(ids);
                if (uniqueIds.size !== ids.length) {
                    console.warn('[TodoStack] ⚠️ Duplicate alert IDs detected!');
                }
                
                setAlerts(pendingAlerts);
            } catch (err) {
                setError('Failed to load tasks');
                console.error('Error fetching alerts:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadAlerts();
    }, []);

    const handleAlertClick = useCallback((alert: AlertItem) => {
        onAlertClick(alert);
    }, [onAlertClick]);

    // Loading state - minimal
    if (isLoading) {
        return (
            <Box className="flex flex-col items-center justify-center py-12">
                <Loader size="md" color="gray" />
                <Text className="text-slate-400" style={{ fontSize: 13, marginTop: spacing.sm }}>
                    Loading tasks...
                </Text>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box className="flex flex-col items-center justify-center py-12">
                <Warning size={32} weight="light" style={{ color: semanticColors.critical }} />
                <Text style={{ fontSize: 13, color: semanticColors.critical, marginTop: spacing.xs }}>
                    {error}
                </Text>
            </Box>
        );
    }

    // Empty state - All caught up
    if (alerts.length === 0) {
        return (
            <Box className="flex flex-col items-center justify-center py-12">
                <Box 
                    style={{
                        backgroundColor: 'rgba(45, 187, 136, 0.1)',
                        borderRadius: '50%',
                        padding: spacing.md,
                        marginBottom: spacing.sm,
                    }}
                >
                    <CheckCircle size={40} weight="fill" style={{ color: semanticColors.positive }} />
                </Box>
                <Text 
                    className="text-slate-900"
                    style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}
                >
                    All caught up
                </Text>
                <Text className="text-slate-400" style={{ fontSize: 13 }}>
                    No pending tasks
                </Text>
            </Box>
        );
    }

    return (
        <Stack style={{ gap: spacing.sm }}>
            {/* Alert cards */}
            <AnimatePresence mode="popLayout">
                {alerts.map((alert, index) => (
                    <AlertCard
                        key={alert.id || `alert-${index}`}
                        alert={alert}
                        index={index}
                        onClick={() => handleAlertClick(alert)}
                    />
                ))}
            </AnimatePresence>
        </Stack>
    );
});

TodoStack.displayName = 'TodoStack';

export default TodoStack;
